import { odooApi } from '../index';
import { extractErrorMessage } from '../transforms';
import { getOdooLang } from '@/config/api.config';

/**
 * Home Slider API Endpoints (Connected to Odoo Backend)
 * 
 * This file contains home slider images API endpoints.
 * Connected to real Odoo API at /api/home/sliders
 */

export interface HomeSliderItem {
  id: number;
  image: string;
  sequence: number;
}

export interface HomeBodyItem {
  id: number;
  title: string;
  subtitle: string;
  icon: string;
  sequence: number;
}

export interface HomeFooterInfo {
  address: string;
  phone: string;
  email: string;
  description: string;
}

export interface HomeSliderResponse {
  slider_images: HomeSliderItem[];
  body_items: HomeBodyItem[];
  footer_info: HomeFooterInfo;
  video: string | null;
  totalRecords?: {
    slider_images: number;
    body_items: number;
  };
}

export const homeSliderApi = odooApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get home slider images from Odoo
    getHomeSliders: builder.query<HomeSliderResponse, { lang?: 'ar' | 'en' | 'ku' }>({
      query: ({ lang = 'ar' }) => {
        const odooLang = getOdooLang(lang);
        return {
          url: `/api/home/sliders?lang=${odooLang}`,
          method: 'POST',
        };
      },
      transformResponse: (response: any) => {
        try {
          if (!response.success || !response.data) {
            throw new Error('Invalid response format');
          }
          
          const data = response.data;
          
          // Sort slider images by sequence
          const sortedSliderImages = [...(data.slider_images || [])].sort((a, b) => 
            (a.sequence || 0) - (b.sequence || 0)
          );
          
          // Sort body items by sequence
          const sortedBodyItems = [...(data.body_items || [])].sort((a, b) => 
            (a.sequence || 0) - (b.sequence || 0)
          );
          
          return {
            slider_images: sortedSliderImages.map((item: any) => ({
              id: item.id || 0,
              image: item.image || '',
              sequence: item.sequence || 0,
            })),
            body_items: sortedBodyItems.map((item: any) => ({
              id: item.id || 0,
              title: item.title || '',
              subtitle: item.subtitle || '',
              icon: item.icon || '',
              sequence: item.sequence || 0,
            })),
            footer_info: data.footer_info || {
              address: '',
              phone: '',
              email: '',
              description: '',
            },
            video: data.video || null,
            totalRecords: response.total_records || {
              slider_images: sortedSliderImages.length,
              body_items: sortedBodyItems.length,
            },
          } as HomeSliderResponse;
        } catch (error) {
          console.error('[Home Slider API] Transform error:', error);
          throw error;
        }
      },
      transformErrorResponse: (response) => {
        return extractErrorMessage(response);
      },
      providesTags: ['Media'],
    }),
  }),
});

export const {
  useGetHomeSlidersQuery,
} = homeSliderApi;

